import "./layout.ui.scss";

import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { userActions, fetchUser } from "stores/slice/user";
import { useIdleTimer } from "react-idle-timer";
import { isNil } from "lodash";
import dayjs from "dayjs";
import { StatusCodes } from 'http-status-codes';

// Components
import Spinner from "../Spinner";

// Slice
import { uiActions } from "stores/slice/ui";
import { pageContextActions } from "stores/slice/pageContext";
import { adminPageContextActions } from "stores/slice/adminPageContext";
import { reloadStatus } from "stores/slice/fetchStatus";

// Model
import User from "models/User";
import Headers from "../Headers";
import Footer from "../Footer";

// page utils
import { PATH } from "router/config/const";
import { organizationType } from "const/nuclias/organization";
import headerService from "../Headers/service";
import { reloadState } from "const/nuclias/system";
import { maxIdleTimeout, userType } from "const/nuclias/user";
import {
  initResources,
  initPageContext,
  initDeviceModel,
  setIndexOrg,
  loadGoogleMap,
} from "./service";

let tokenExpiredTimer = null;

const Layout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, pageContext, fetchStatus } = useSelector(store => store);
  const [layoutReady, setLayoutReady] = useState(false);

  const logout = () => {
    User.removeTokenLocalStoreage();
    dispatch(pageContextActions.cleanAll());
    dispatch(adminPageContextActions.cleanAll());
    sessionStorage.removeItem('mspSelectedOrg');
    sessionStorage.removeItem('access_token');
    navigate(PATH.login);
  };

  const onIdle = () => {
    if (!User.getTokenKeepAlive()) {
      logout();
    }
  };
  useIdleTimer({ timeout: maxIdleTimeout * 1000, onIdle });

  const checkToken = async () => {
    const token = User.getTokenAndAliveTime();

    if (isNil(token)) {
      logout();
    } else {
      try {
        const now = dayjs(new Date()).unix();
        const diff = token.tokenAliveTime - now;
        if (diff < 1) {
          logout();
          return;
        } else {
          if (!isNil(tokenExpiredTimer)) { clearTimeout(tokenExpiredTimer); }
          console.debug(`Set tokenExpiredTimer, logout after ${Math.floor(diff / 3600)} hrs ${Math.floor(diff % 3600 / 60)} mins.`);
          tokenExpiredTimer = setTimeout(function () {
            logout();
          }, diff * 1000);
        }
        User.setToken(token.token);
        dispatch(userActions.setToken(token));
        loadGoogleMap()
        const user = await dispatch(fetchUser()).unwrap();
        initLayout(user);
      } catch (error) {
        if (error?.code === StatusCodes.UNAUTHORIZED) {
          logout();
        } else {
          // navigate('/login'); // tmp~~, for server timeout etc...
          dispatch(uiActions.hideSpinner());
        }
      } finally {
        dispatch(uiActions.hideSpinner());
      }
    }
  };


  const initLayout = async (user) => {
    dispatch(uiActions.showSpinner());
    await Promise.all([
      initResources(),
      initPageContext(),
      initDeviceModel(),
    ]);
    userDefaultRedirect(user);
    setLayoutReady(true);
    dispatch(uiActions.hideSpinner());
  };

  const userDefaultRedirect = (user) => {
    const currentPath = window.location.pathname;
    // console.log('initLayout', currentPath, user);
    if (currentPath === '/') {
      if (user.type === userType.cloudAdmin) {
        navigate('/admin/dashboard');
      } else if (user.type === userType.sp) {
        navigate(PATH.settings.organization);
      } else {
        navigate(PATH.dashboard.dashboard);
      }
    }
  };

  const reload = async (unMultiStep) => {
    return new Promise(async (Resolve, Reject) => {
      dispatch(uiActions.showSpinner());
      dispatch(reloadStatus(reloadState.doing));
      await initPageContext();
      if (unMultiStep) {
        dispatch(reloadStatus(reloadState.done));
      }
      dispatch(uiActions.hideSpinner());
      setLayoutReady(true);
      Resolve();
    });
  };

  const reloadIndexOrg = async () => {
    dispatch(uiActions.showSpinner());
    dispatch(reloadStatus(reloadState.doing));
    const data = fetchStatus.loadOrgBootstarp.data;
    if (!isNil(data.reloadGui) && data.reloadGui) {
      await reload();
      await setIndexOrg(data.orgId);
    } else {
      if (isNil(data.orgId)) {
        await reload();
      } else {
        await setIndexOrg(data.orgId);
      }
    }
    dispatch(reloadStatus(reloadState.done));
    dispatch(uiActions.hideSpinner());
  };

  const loadSite = async () => {
    const data = fetchStatus.loadTargetSite.data;
    const type = isNil(data.type) ? organizationType.site : data.type;
    const id = data.id;

    if (isNil(pageContext.selectedOrg)) {
      console.warn(`Cannot find selected Org, skip change.`);
      return;
    }

    if (isNil(id)) {
      console.debug(`Change to ORG level.`);
    } else {
      if (type === organizationType.site && isNil(pageContext.selectedOrg?.storeObj[id])) {
        console.warn(`Cannot find Site id: ${id}, skip change.`);
        return;
      }
      if (type === organizationType.tag && isNil(pageContext.selectedOrg?.storeTagObj[id])) {
        console.warn(`Cannot find Tag id: ${id}, skip change.`);
        return;
      }
    }

    headerService.changeSite({ type, id });
  };

  useEffect(() => {
    checkToken();
  }, []);

  useEffect(() => {
    if (pageContext.sp || pageContext.si || pageContext.selectedOrg) {
      reload(true);
    }
  }, [fetchStatus.loadGuiBootstarp]);

  useEffect(() => {
    if (!isNil(fetchStatus.loadOrgBootstarp.timestamp)) {
      reloadIndexOrg();
    }
  }, [fetchStatus.loadOrgBootstarp]);

  useEffect(() => {
    if (!isNil(fetchStatus.loadTargetSite.timestamp)) {
      loadSite();
    }
  }, [fetchStatus.loadTargetSite]);

  return (
    <>
      <div className="main-container">
        {layoutReady ?
          <>
            {user.user && user?.user?.type !== userType.cloudAdmin && <Headers />}
            <div id="main-layout-container">
              <Outlet />
            </div>
            <Footer />
          </> : <Spinner />
        }
      </div>
    </>
  );
};

export default Layout;